import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "./init";
import { convertTimestampToDate } from "./firestore";
import { Product } from "@/types/product";
import { buildPublicStorageUrl } from "@/utils/storageUtils";

/**
 * Firestore 商品數據類型（包含 Timestamp）
 */
interface FirestoreProductData {
  name?: string;
  price?: number;
  status?: "active" | "inactive";
  stock_quantity?: number;
  main_image?: string;
  additional_images?: string[];
  description?: string;
  category?: "merchandise" | "clothing" | "collectibles" | "digital" | "other";
  created_at?: Timestamp | Date | null;
  updated_at?: Timestamp | Date | null;
}

/**
 * 轉換商品數據（處理 Timestamp）
 */
function convertProductData(data: FirestoreProductData, id: string): Omit<Product, "id"> {
  return {
    name: data.name || "",
    price: data.price || 0,
    status: data.status || "active",
    stock_quantity: data.stock_quantity,
    main_image: data.main_image || "",
    additional_images: data.additional_images,
    description: data.description || "",
    category: data.category,
    created_at: convertTimestampToDate(data.created_at) as Date | undefined,
    updated_at: convertTimestampToDate(data.updated_at) as Date | undefined,
  };
}

/**
 * 獲取所有商品
 */
export async function getProducts(): Promise<Product[]> {
  try {
    const productsRef = collection(db, "products");
    let querySnapshot;
    let useClientSort = false;

    try {
      const q = query(productsRef, orderBy("name", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      console.warn("Failed to order by 'name', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(productsRef));
      useClientSort = true;
    }

    const products = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertProductData(doc.data(), doc.id),
    })) as Product[];

    if (useClientSort) {
      products.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return products;
  } catch (error) {
    console.error("Error getting products:", error);
    throw error;
  }
}

/**
 * 獲取單個商品
 */
export async function getProduct(id: string): Promise<Product | null> {
  try {
    const productRef = doc(db, "products", id);
    const productSnap = await getDoc(productRef);

    if (productSnap.exists()) {
      return {
        id: productSnap.id,
        ...convertProductData(productSnap.data(), productSnap.id),
      } as Product;
    }

    return null;
  } catch (error) {
    console.error("Error getting product:", error);
    throw error;
  }
}

/**
 * 創建商品
 */
export async function createProduct(
  data: Omit<Product, "id" | "created_at" | "updated_at">
): Promise<string> {
  try {
    if (!data.name || data.name.trim().length === 0) {
      throw new Error("商品名稱為必填");
    }
    if (data.price === undefined || data.price < 0) {
      throw new Error("價格必須大於等於 0");
    }
    if (!data.main_image || data.main_image.trim().length === 0) {
      throw new Error("商品主圖為必填");
    }
    if (!data.description || data.description.trim().length === 0) {
      throw new Error("商品描述為必填");
    }

    const productsRef = collection(db, "products");
    const productData: {
      name: string;
      price: number;
      status: "active" | "inactive";
      stock_quantity?: number;
      main_image: string;
      additional_images?: string[];
      description: string;
      category?: "merchandise" | "clothing" | "collectibles" | "digital" | "other";
      created_at: Timestamp;
      updated_at: Timestamp;
    } = {
      name: data.name.trim(),
      price: data.price,
      status: data.status || "active",
      main_image: data.main_image,
      description: data.description.trim(),
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    };

    if (data.stock_quantity !== undefined) productData.stock_quantity = data.stock_quantity;
    if (data.additional_images && data.additional_images.length > 0) {
      productData.additional_images = data.additional_images;
    }
    if (data.category) productData.category = data.category;

    const docRef = doc(productsRef);
    await setDoc(docRef, productData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating product:", error);
    throw error;
  }
}

/**
 * 更新商品
 */
export async function updateProduct(
  id: string,
  data: Partial<Omit<Product, "id" | "created_at">>
): Promise<void> {
  try {
    const productRef = doc(db, "products", id);
    const updateData: {
      updated_at: Timestamp;
      name?: string;
      price?: number;
      status?: "active" | "inactive";
      stock_quantity?: number;
      main_image?: string;
      additional_images?: string[];
      description?: string;
      category?: "merchandise" | "clothing" | "collectibles" | "digital" | "other";
    } = {
      updated_at: Timestamp.now(),
    };

    if (data.name !== undefined) updateData.name = data.name.trim();
    if (data.price !== undefined) updateData.price = data.price;
    if (data.status !== undefined) updateData.status = data.status;
    if (data.stock_quantity !== undefined) {
      updateData.stock_quantity = data.stock_quantity >= 0 ? data.stock_quantity : undefined;
    }
    if (data.main_image !== undefined) {
      updateData.main_image = data.main_image || undefined;
    }
    if (data.additional_images !== undefined) {
      updateData.additional_images = data.additional_images.length > 0 ? data.additional_images : undefined;
    }
    if (data.description !== undefined) updateData.description = data.description.trim();
    if (data.category !== undefined) updateData.category = data.category;

    await updateDoc(productRef, updateData);
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  }
}

/**
 * 刪除商品
 */
export async function deleteProduct(id: string): Promise<void> {
  try {
    const productRef = doc(db, "products", id);
    await deleteDoc(productRef);
  } catch (error) {
    console.error("Error deleting product:", error);
    throw error;
  }
}

/**
 * 同步建構商品圖片 URL
 *
 * products bucket 為公開可讀,直接組 CDN URL,瀏覽器自動 disk-cache。
 */
export function getProductImageUrl(
  imageUrl: string | undefined,
  defaultPrefix: string = "product_images"
): string {
  return buildPublicStorageUrl(imageUrl, defaultPrefix);
}

